import { useEffect, useRef, useState, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/* CSS */
import styles from './CellRenderer.module.css';

/* API */
import { api } from "../../api";

/* Icons */
import search_icon from '../../assets/icons/search_icon.svg';
import reorder_icon from '../../assets/icons/reorder_icon.svg';
import delete_icon from '../../assets/icons/delete_icon.svg';
import edit_icon from '../../assets/icons/edit_icon.svg';

import time_input from '../../assets/icons/time_input.svg';
import chevron_down from '../../assets/icons/chevron_down.svg';

import tick_icon_white from '../../assets/icons/tick_icon_white.svg';
import default_avatar from '../../assets/icons/default_avatar.svg';

/* Components */
import ConfirmationModal from '../cards/ConfirmationModal';

/* Context */
import { useToast } from '../../context/ToastContext';

/* Utils */
import { timeStringToDate, parseTime } from '../../utils/dateUtils';
import { packTagLines } from '../../utils/columnUtils';

// TODO: should we refactor these cells in separate files?

/* Text input cell with auto-resizing */
export const TextCell = ({ value, onChange, placeholder, onContextMenu }) => {
    const textAreaRef = useRef(null);

    const adjustHeight = () => {
        if (textAreaRef.current) {
            // Reset height to auto to shrink if text was deleted
            textAreaRef.current.style.height = 'auto';
            // Set height to scrollHeight to fit content
            textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
        }
    };

    useEffect(() => {
        adjustHeight();
    }, [value]);

    const prevWidthRef = useRef(0);

    useEffect(() => {
        if (!textAreaRef.current) return;

        // Observe width changes to trigger height adjustment (e.g. column resize)
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                // Only adjust if width changed significantly to avoid loops involved with height changes
                if (width !== prevWidthRef.current) {
                    prevWidthRef.current = width;
                    adjustHeight();
                }
            }
        });

        observer.observe(textAreaRef.current);
        return () => observer.disconnect();
    }, []); // Empty dependency array to run once on mount

    return (
        <textarea
            ref={textAreaRef}
            className={styles.itineraryInput}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={1} // Start with one row
            onContextMenu={onContextMenu} // For right click menu
        />
    );
};

/* Time input cell */
export const TimeCell = ({ value, onChange, onContextMenu, isGuideMode }) => {

    const { hour, minute, ampm } = parseTime(value);

    const [activeSection, setActiveSection] = useState(null); // 'hours', 'minutes', 'ampm'
    const [dropdownStyle, setDropdownStyle] = useState({});

    // Helper to validate time
    const toValidDate = (v) => {
        const d = timeStringToDate(v);
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    };

    // State for the DatePicker object
    const [selectedTime, setSelectedTime] = useState(toValidDate(value));

    // Refs for anchoring the dropdown to the specific text clicked
    const containerRef = useRef(null);
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const ampmRef = useRef(null);
    const dropdownRef = useRef(null); // Ref for portal dropdown

    // Visible if: It's the "Guide Row" OR user has already chosen a time OR user is currently typing
    const isVisible = isGuideMode || value || activeSection;

    useLayoutEffect(() => {
        if (!activeSection) return;

        let anchorRef = null;
        if (activeSection === 'hour') anchorRef = hourRef;
        if (activeSection === 'minute') anchorRef = minuteRef;
        if (activeSection === 'ampm') anchorRef = ampmRef;

        if (anchorRef && anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setDropdownStyle({
                top: `${rect.bottom + window.scrollY + 4}px`,
                left: `${rect.left + window.scrollX}px`,
            });
        }
    }, [activeSection]);

    useEffect(() => {
        if (!activeSection) return;

        const handleClick = (e) => {
            // Check if click is inside container OR inside the dropdown portal
            const inContainer = containerRef.current && containerRef.current.contains(e.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);

            if (!inContainer && !inDropdown) {
                setActiveSection(null);
            }
        };

        document.addEventListener('mousedown', handleClick, true);
        return () => document.removeEventListener('mousedown', handleClick, true);
    }, [activeSection]);

    useEffect(() => {
        setSelectedTime(toValidDate(value))
    }, [value])

    const [localTime, setLocalTime] = useState(null);

    // Update the whole time cell if either of [h,m,ap] is chosen
    const handleUpdate = (section, newVal) => {
        const current = parseTime(value);

        // If converting from empty state, default the others
        const h = section === 'hour' ? newVal : (current.hour === '--' ? '12' : current.hour);
        const m = section === 'minute' ? newVal : (current.minute === '--' ? '00' : current.minute);

        let ap;
        if (section === 'ampm') {
            ap = newVal;
        } else {
            // Preserve current, or default to AM if missing
            ap = current.ampm === '--' ? 'AM' : current.ampm;
        }

        const timeString = `${h}:${m} ${ap}`

        onChange(timeString);
        setLocalTime(null); // Clear local state to prevent conflicts
        setActiveSection(null); // Close after selection
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            setActiveSection(null);
            e.currentTarget.blur(); // Also blur to ensure state committed
        }
    };

    // Handle manual typing
    const handleManualChange = (section, val) => {
        // Validation logic
        let newVal = val;

        if (section === 'hour') {
            // Digits only
            if (!/^\d*$/.test(newVal)) return;
            if (newVal.length > 2) return;

            // Allow typing "1" or "12". Logic to jump:
            if (newVal.length === 2) {
                // Check validity (0-12)
                let num = parseInt(newVal, 10);
                if (num > 12) num = 12;
                newVal = num.toString().padStart(2, '0');
                if (minuteRef.current) minuteRef.current.focus();
            }
        } else if (section === 'minute') {
            if (!/^\d*$/.test(newVal)) return;
            if (newVal.length > 2) return;
            if (newVal.length === 2) {
                let num = parseInt(newVal, 10);
                if (num > 59) num = 59;
                newVal = num.toString().padStart(2, '0');
                if (ampmRef.current) ampmRef.current.focus();
            }
        } else if (section === 'ampm') {

            const parsed = parseTime(value);
            const currentVal = localTime?.ampm !== undefined ? localTime.ampm : parsed.ampm;

            // Handle shortcuts a/p
            // Ignore clears and backspaces; only react to added characters
            if (newVal !== '' && newVal.length >= currentVal.length) {
                const lower = newVal.toLowerCase();
                if (lower.includes('p')) {
                    newVal = 'PM';
                    setActiveSection(null);
                } else {
                    // Tie break by automatically choosing AM for anything else (A, or junk)
                    newVal = 'AM';
                    setActiveSection(null);
                }
            }
        }

        // Update local state for fluid typing
        setLocalTime(prev => ({
            ...prev,
            [section]: newVal
        }));

        let {
            hour: h,
            minute: m,
            ampm: ap
        } = {
            ...parseTime(value),
            ...localTime,
            [section]: newVal
        };

        const timeString = `${h}:${m} ${ap}`;
        onChange(timeString);
    };

    // Handler for DatePicker change
    const handlePickerChange = (date) => {
        setSelectedTime(date);
        if (date) {
            // Format Date back to "HH:mm AM/PM" string for parent state
            let hours = date.getHours();
            let minutes = date.getMinutes();
            const ampmStr = hours >= 12 ? 'PM' : 'AM';

            hours = hours % 12;
            hours = hours ? hours : 12; // the hour '0' should be '12'

            const minutesStr = minutes < 10 ? '0' + minutes : minutes;
            const strTime = `${hours}:${minutesStr} ${ampmStr}`;

            onChange(strTime);
        } else {
            onChange('');
        }
    };

    // Generate options
    const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    const ampms = ['AM', 'PM'];

    const renderDropdown = () => {
        if (!activeSection || !dropdownStyle.left) return null;

        let content = null;
        let isScrollable = false;

        if (activeSection === 'hour') {
            isScrollable = true;
            content = hours.map(h => (
                <div
                    key={h}
                    className={styles.timeOption}
                    onMouseDown={() => handleUpdate('hour', h)}
                >
                    {h}
                </div>
            ));
        } else if (activeSection === 'minute') {
            isScrollable = true;
            content = minutes.map(m => (
                <div
                    key={m}
                    className={styles.timeOption}
                    onMouseDown={() => handleUpdate('minute', m)}
                >
                    {m}
                </div>
            ));
        } else if (activeSection === 'ampm') {
            content = ampms.map(ap => (
                <div
                    key={ap}
                    className={styles.timeOption}
                    onMouseDown={() => handleUpdate('ampm', ap)}
                >
                    {ap}
                </div>
            ));
        }

        return createPortal(
            <div
                className={styles.timeDropdown}
                style={dropdownStyle}
                ref={dropdownRef}
            >
                <div
                    className={isScrollable ? styles.timeDropdownScroll : ""}
                >
                    {content}
                </div>
            </div>,
            document.body
        );
    };

    const hideClock = !isGuideMode && !value && !activeSection;

    return (
        <div
            className={`${styles.timeCellContainer} ${hideClock ? styles.hideClock : ''}`}
            onContextMenu={(e) => {
                setActiveSection(null); // close an already-open time dropdown so only the row menu shows
                onContextMenu?.(e);
            }}
            ref={containerRef}
        >
            <div
                className={`${styles.manualTimeInput} ${isVisible ? styles.active : styles.inactive}`}
                onMouseDown={(e) => {
                    // Right-click should only open the row context menu. Block the
                    // focus (mousedown's default action) so onFocus doesn't fire and
                    // pop the hour/minute/ampm dropdown at the same time.
                    if (e.button === 2) e.preventDefault();
                }}
            >
                {/* Hours Section */}
                <div className={`${styles.timeSection} ${styles.hour}`}>
                    <input
                        ref={hourRef}
                        className={`${styles.timeDisplay} ${activeSection === 'hour' ? styles.active : ''}`}
                        value={activeSection === 'hour' && localTime ? localTime.hour : hour}
                        onChange={(e) => handleManualChange('hour', e.target.value)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveSection('hour');
                        }}
                        onFocus={(e) => {
                            e.target.select();
                            setActiveSection('hour');
                            setLocalTime({ hour, minute, ampm });
                        }}
                        onBlur={() => {
                            setTimeout(() => {
                                if (document.activeElement !== hourRef.current && document.activeElement !== minuteRef.current && document.activeElement !== ampmRef.current) {
                                    setActiveSection(null);
                                }
                            }, 50);
                        }}
                        placeholder="--"
                        maxLength={2}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <span>:</span>

                {/* Minutes Section */}
                <div className={`${styles.timeSection} ${styles.minute}`}>
                    <input
                        ref={minuteRef}
                        className={`${styles.timeDisplay} ${activeSection === 'minute' ? styles.active : ''}`}
                        value={activeSection === 'minute' && localTime ? localTime.minute : minute}
                        onChange={(e) => handleManualChange('minute', e.target.value)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveSection('minute');
                        }}
                        onFocus={(e) => {
                            e.target.select();
                            setActiveSection('minute');
                            setLocalTime({ hour, minute, ampm });
                        }}
                        onBlur={() => {
                            setTimeout(() => {
                                if (document.activeElement !== hourRef.current && document.activeElement !== minuteRef.current && document.activeElement !== ampmRef.current) {
                                    setActiveSection(null);
                                }
                            }, 50);
                        }}
                        placeholder="--"
                        maxLength={2}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                {/* AM/PM Section */}
                <div className={styles.timeSectionAmpm}>
                    <input
                        ref={ampmRef}
                        className={`${styles.timeDisplay} ${activeSection === 'ampm' ? styles.active : ''}`}
                        value={activeSection === 'ampm' && localTime ? localTime.ampm : ampm}
                        onChange={(e) => handleManualChange('ampm', e.target.value)}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveSection('ampm');
                        }}
                        onFocus={(e) => {
                            e.target.select();
                            setActiveSection('ampm');
                            setLocalTime({ hour, minute, ampm });
                        }}
                        onBlur={() => {
                            setTimeout(() => {
                                if (document.activeElement !== hourRef.current && document.activeElement !== minuteRef.current && document.activeElement !== ampmRef.current) {
                                    setActiveSection(null);
                                }
                            }, 50);
                        }}
                        placeholder="--"
                        maxLength={2}
                        onKeyDown={handleKeyDown}
                    />
                </div>
            </div>

            <div className={styles.pickerTimeInput}>
                {/* Timer Icon */}
                <DatePicker
                    selected={selectedTime}
                    onChange={handlePickerChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeCaption="Time"
                    timeIntervals={5}
                    dateFormat="hh:mm aa"
                    customInput={
                        <div className={styles.timeIconWrapper}>
                            <img
                                src={time_input}
                                className={styles.timeInputIcon}
                                alt="Time Picker"
                            />
                        </div>
                    }
                    popperContainer={({ children }) => createPortal(children, document.body)} // Render outside overflow boundaries
                    popperPlacement="bottom-end"
                    popperClassName="custom-time-picker-popper"
                    calendarClassName="custom-time-picker-calendar"
                />
            </div>

            {renderDropdown()}

        </div>
    )
};

/* Number input cell */
export const NumberCell = ({ value, onChange, placeholder, onContextMenu }) => (
    <input
        type="number"
        className={`${styles.itineraryInput} ${styles.numberCell}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onContextMenu={onContextMenu}
    />
);

/* Tag selection cell */
export const TagCell = ({ value, onChange, onContextMenu, options, onOptionsChange, onTagRename, onTagDelete, isGuideMode, isCostCategory }) => {

    const { showToast } = useToast();

    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [renameModalData, setRenameModalData] = useState({ visible: false, oldTag: '', newTag: '', error: '' });
    const [deleteModalData, setDeleteModalData] = useState({ visible: false, tagToDelete: '' });

    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const selectedTags = Array.isArray(value) ? value : [];

    // ---- Tag line breaking ----
    // - We break the pills into lines ourselves (instead of CSS flex-wrap) so each
    //   line can reserve room for the hover-only delete button. 
    // - pillAreaRef is the lines' container
    // - a ResizeObserver keeps its width live as the column resizes.
    const pillAreaRef = useRef(null);
    const [pillAreaWidth, setPillAreaWidth] = useState(0);

    useLayoutEffect(() => {
        const el = pillAreaRef.current;
        if (!el) return;
        const measure = () => setPillAreaWidth(el.clientWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [selectedTags.length]);

    const tagLines = useMemo(
        () => packTagLines(selectedTags, pillAreaWidth),
        [selectedTags, pillAreaWidth]
    );

    // Position dropdown
    useLayoutEffect(() => {
        if (isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();

            const spaceBelow = window.innerHeight - rect.bottom;
            const showAbove = spaceBelow < 250;

            if (showAbove) {
                setDropdownStyle({
                    top: `${rect.top + window.scrollY - 4}px`,
                    left: `${rect.left + window.scrollX}px`,
                    transform: 'translateY(-100%)'
                });
            } else {
                setDropdownStyle({
                    top: `${rect.bottom + window.scrollY + 4}px`,
                    left: `${rect.left + window.scrollX}px`,
                });
            }
        }
    }, [isOpen, value]);

    // Focus input without scrolling the page
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return;

        const handleClick = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target) &&
                !e.target.closest('[data-tag-dropdown]') &&
                !e.target.closest('[data-modal-overlay]') // Ignore clicks on the modal so dropdown stays open
            ) {
                setIsOpen(false);
                setDragOverIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClick, true);
        return () => document.removeEventListener('mousedown', handleClick, true);
    }, [isOpen]);

    const handleSelect = (tag) => {
        if (selectedTags.includes(tag)) return; // Prevent duplicates

        // Heads-up: the cost breakdown uses "first tag wins", so extra category tags are ignored there.
        // Only warn once — when the user adds the SECOND tag (going from 1 → 2), not on every tag after.
        if (isCostCategory && selectedTags.length === 1) {
            showToast('Heads up: only the first category counts in your cost breakdown.', 'info');
        }

        const newTags = [...selectedTags, tag];
        onChange(newTags);
        setFilter('');
        if (inputRef.current) inputRef.current.focus();
    };

    const handleCreate = () => {
        if (filter && options && !options.includes(filter)) {
            if (onOptionsChange) {
                onOptionsChange([...options, filter]);
            }
            handleSelect(filter);
        }
    };

    const handleSort = () => {
        // Duplicate options
        let _options = [...options];

        // Remove dragged item
        const draggedItemContent = _options.splice(dragItem.current, 1)[0];

        // Insert dragged item at new position
        _options.splice(dragOverItem.current, 0, draggedItemContent);

        // Reset refs
        dragItem.current = null;
        dragOverItem.current = null;
        setDragOverIndex(null);

        // Update options
        if (onOptionsChange) {
            onOptionsChange(_options);
        }
    };

    const safeOptions = options || [];
    const filteredOptions = safeOptions.filter(option =>
        option.toLowerCase().includes(filter.toLowerCase())
    );

    /* Helper to determine class for drag line */
    const getDragClass = (index) => {
        if (dragOverIndex !== index) return '';
        // If dragging DOWN (source < target), show line at BOTTOM
        if (dragItem.current < index) return styles.dragOverBottom;
        // If dragging UP (source > target), show line at TOP
        if (dragItem.current > index) return styles.dragOverTop;
        return '';
    };

    const dropdownContent = (
        <div className={styles.tagDropdown} style={dropdownStyle} data-tag-dropdown>
            <div>
                <img
                    src={search_icon}
                    alt="Search Icon"
                    className={styles.tagSearchIcon}
                />

                <input
                    className={styles.tagSearchInput}
                    placeholder="Select or create..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreate();
                    }}
                />
            </div>

            <div>
                {filteredOptions.map((option, index) => (
                    <div
                        key={option}
                        className={`${styles.tagOption} ${getDragClass(index)}`}
                        draggable
                        onDragStart={(e) => {
                            e.stopPropagation(); // Stop parent from dragging
                            dragItem.current = index;
                        }}
                        onDragEnter={(_e) => {
                            dragOverItem.current = index;
                            setDragOverIndex(index);
                        }}
                        onDragEnd={handleSort}
                        onDragOver={(e) => e.preventDefault()}
                        onPointerDown={(e) => e.stopPropagation()} // Stop dnd-kit sensors
                        onMouseDown={(e) => e.stopPropagation()}   // Stop dnd-kit sensors
                        onClick={() => handleSelect(option)}
                    >
                        <img src={reorder_icon} alt="Reorder Tag Icon" className={styles.tagDragIcon} />
                        <span className={styles.tagOptionText}>{option}</span>
                        <div className={styles.tagOptionActions}>
                            <img
                                src={edit_icon}
                                alt="Edit Tag Icon"
                                className={styles.tagActionIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRenameModalData({ visible: true, oldTag: option, newTag: option });
                                }}
                            />
                            <img
                                src={delete_icon}
                                alt="Delete Tag Icon"
                                className={styles.tagActionIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteModalData({ visible: true, tagToDelete: option });
                                }}
                            />
                        </div>
                    </div>
                ))}
                {filter && !filteredOptions.length && (
                    <div className={styles.tagCreateOption} onClick={handleCreate}>
                        Enter to create: "{filter}"
                    </div>
                )}
            </div>
        </div>
    );

    // Confirmation logic for deleting a tag
    const handleConfirmDelete = () => {
        const { tagToDelete } = deleteModalData;
        if (tagToDelete && onTagDelete) {
            // The parent's unified handler syncs BOTH the options array AND row values cleanly
            onTagDelete(tagToDelete);
        } else if (tagToDelete && onOptionsChange) {
            // Fallback for simple contexts
            onOptionsChange(options.filter(opt => opt !== tagToDelete));
            if (selectedTags.includes(tagToDelete)) {
                onChange(selectedTags.filter(t => t !== tagToDelete));
            }
        }
        setDeleteModalData({ visible: false, tagToDelete: '' });
    };

    // Confirmation logic for the modal
    const handleConfirmRename = () => {
        const { oldTag, newTag } = renameModalData;
        const cleanName = newTag.trim();

        if (cleanName && cleanName !== oldTag) {
            const safeOptions = options || [];

            // Validation: Check if the new tag already exists (case-insensitive)
            if (safeOptions.some(opt => opt.toLowerCase() === cleanName.toLowerCase())) {
                setRenameModalData(prev => ({ ...prev, error: `The tag "${cleanName}" already exists.` }));
                return;
            }

            if (onTagRename) {
                // The parent's unified handler syncs BOTH the options array AND row values cleanly
                onTagRename(oldTag, cleanName);
            } else if (onOptionsChange) {
                // Fallback for simple contexts
                const newOptions = options.map(opt => opt === oldTag ? cleanName : opt);
                onOptionsChange(newOptions);
                if (selectedTags.includes(oldTag)) {
                    onChange(selectedTags.map(t => t === oldTag ? cleanName : t));
                }
            }
        }
        setRenameModalData({ visible: false, oldTag: '', newTag: '', error: '' });
    };

    return (
        <div
            className={styles.tagCellWrapper}
            ref={wrapperRef}
            onContextMenu={onContextMenu}
        >
            {/* Tag Rename Modal */}
            <ConfirmationModal
                isOpen={renameModalData.visible}
                onClose={() => setRenameModalData({ visible: false, oldTag: '', newTag: '', error: '' })}
                onConfirm={handleConfirmRename}
                title={`Change "${renameModalData.oldTag}" to:`}
                confirmText="Save"
                cancelText="Cancel"
                customWidth="400px"
            >
                <input
                    type="text"
                    value={renameModalData.newTag}
                    onChange={(e) => setRenameModalData(prev => ({ ...prev, newTag: e.target.value, error: '' }))}
                    className={`${styles.tagRenameInput} ${renameModalData.error ? styles.error : ''}`}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleConfirmRename();
                        if (e.key === 'Escape') setRenameModalData({ visible: false, oldTag: '', newTag: '', error: '' });
                    }}
                    autoFocus
                />
                {renameModalData.error && (
                    <div className={styles.tagRenameError}>
                        {renameModalData.error}
                    </div>
                )}
            </ConfirmationModal>

            {/* Tag Delete Modal */}
            <ConfirmationModal
                isOpen={deleteModalData.visible}
                onClose={() => setDeleteModalData({ visible: false, tagToDelete: '' })}
                onConfirm={handleConfirmDelete}
                title={`Delete "${deleteModalData.tagToDelete}"?`}
                message={`This will permanently remove the tag from all activities. This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                isDanger={true}
                customWidth="400px"
            />

            <div
                className={styles.tagClickableArea}
                onClick={() => setIsOpen(true)}
            >
                {selectedTags.length > 0 ? (
                    <div className={styles.tagPillContainer} ref={pillAreaRef}>
                        {tagLines.map((line, lineIndex) => (
                            <div key={lineIndex} className={styles.tagPillLine}>
                                {line.map(tag => (
                                    <span key={tag} className={styles.tagPill}>
                                        {tag}

                                        <span
                                            className={styles.tagPillRemove}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange(selectedTags.filter(t => t !== tag));
                                            }}
                                        >×</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    isGuideMode ? <span className={styles.tagPlaceholder}>Select...</span> : null
                )}
            </div>

            {isOpen && createPortal(dropdownContent, document.body)}
        </div >
    );
};

/* URL input cell */
export const UrlCell = ({ value, onChange, placeholder, onContextMenu, isGuideMode }) => {

    const urls = Array.isArray(value) ? value : (value ? [value] : []);

    const [editingIndex, setEditingIndex] = useState(null);
    const [editValue, setEditValue] = useState('');

    const inputRef = useRef(null);

    useEffect(() => {
        if (editingIndex != null && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingIndex]);

    // Ensure URL has protocol for the <a> tag
    const getHref = (val) => {
        if (!val) return '#';
        if (val.startsWith('http://') || val.startsWith('https://')) {
            return val;
        }
        return 'https://' + val;
    }

    const handleAdd = () => {
        const newUrls = [...urls, ""];
        onChange(newUrls);
        setEditingIndex(newUrls.length - 1);
        setEditValue('');
    };

    const handleEditStart = (index, currentValue) => {
        setEditingIndex(index);
        setEditValue(currentValue);
    };

    const handleSave = (index) => {
        const newUrls = [...urls];
        if (editValue.trim() === '') {
            newUrls.splice(index, 1);
        } else {
            newUrls[index] = editValue;
        }
        onChange(newUrls);
        setEditingIndex(null);
    };

    const handleDelete = (index) => {
        const newUrls = [...urls];
        newUrls.splice(index, 1);
        onChange(newUrls);
        setEditingIndex(null);
    }

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter') handleSave(index);
        if (e.key === 'Escape') {
            if (urls[index] === '') {
                handleDelete(index);
            } else {
                setEditingIndex(null);
            }
        }
    };

    return (
        <div
            className={`${styles.urlCellWrapper} ${isGuideMode ? styles.guideMode : ''}`}
            onContextMenu={onContextMenu}
            onClick={() => {
                if (urls.length === 0 && editingIndex == null) {
                    handleAdd();
                }
            }}
        >
            <div className={styles.urlList}>
                {urls.map((url, index) => {
                    const isEditing = editingIndex === index;

                    if (isEditing) {
                        return (
                            <div key={index} className={styles.urlRow}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className={styles.itineraryInput}
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleSave(index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    placeholder={placeholder || "www.example.com"}
                                />
                            </div>
                        )
                    }

                    return (
                        <div key={index} className={styles.urlRow}>
                            <a
                                href={getHref(url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.urlLink}
                                title={url}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {url}
                            </a>

                            <div className={styles.urlActions}>

                                {/* Edit Button */}
                                <button
                                    className={styles.urlActionButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditStart(index, url);
                                    }}
                                    title="Edit URL"
                                >
                                    <img
                                        src={edit_icon}
                                        alt="Edit URL"
                                    />
                                </button>

                                {/* Delete Button */}
                                <button
                                    className={styles.urlActionButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(index);
                                    }}
                                    title="Delete URL"
                                >
                                    <img
                                        src={delete_icon}
                                        alt="Delete URL"
                                    />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Add Link Button - Always rendered, visibility handled by CSS */}
            {editingIndex == null && (
                <button
                    className={styles.urlAddButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleAdd();
                    }}
                >
                    + Add URL
                </button>
            )}
        </div>
    );
};

/* Member selection cell */
export const PersonCell = ({ value, onChange, onContextMenu, availableUsers = [], isGuideMode }) => {

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});

    const wrapperRef = useRef(null);
    const users = availableUsers || [];

    // Ensure value is an array
    const selectedUsers = Array.isArray(value) ? value : (value ? [value] : []);

    // Get user details for selected IDs
    const selectedUserDetails = selectedUsers.map(uId =>
        users.find(u => u.id === uId)
    ).filter(Boolean);

    // Position dropdown
    useLayoutEffect(() => {
        if (isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();

            setDropdownStyle({
                top: `${rect.bottom + window.scrollY + 4}px`,
                left: `${rect.left + window.scrollX}px`,
            });
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target) && !e.target.closest('[data-person-dropdown]')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick, true);
        return () => document.removeEventListener('mousedown', handleClick, true);
    }, [isOpen]);

    const handleToggleUser = (userId) => {
        const isSelected = selectedUsers.includes(userId);
        let newValue;
        if (isSelected) {
            newValue = selectedUsers.filter(id => id !== userId);
        } else {
            newValue = [...selectedUsers, userId];
        }
        onChange(newValue);
    };

    const dropdownContent = (
        <div
            className={styles.personDropdown}
            style={dropdownStyle}
            data-person-dropdown
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {users.map(user => {
                const isSelected = selectedUsers.includes(user.id);
                return (
                    <div
                        key={user.id}
                        className={styles.personOption}
                        onClick={() => handleToggleUser(user.id)}
                    >
                        <div className={`${styles.personCheckbox} ${isSelected ? styles.checked : ''}`}>
                            {isSelected && <img src={tick_icon_white} alt="Checked" />}
                        </div>

                        <span className={styles.personName}>{user.name}</span>

                        <img
                            src={user.avatar || default_avatar}
                            alt={user.name}
                            className={styles.personAvatarSmall}
                        />
                    </div>
                );
            })}
        </div>
    );

    return (
        <div
            className={styles.personCellWrapper}
            ref={wrapperRef}
            onContextMenu={onContextMenu}
        >
            <div
                className={styles.personDisplay}
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedUserDetails.length > 0 ? (
                    <div className={styles.personAvatarsStack}>
                        {selectedUserDetails.map((user, index) => (
                            <div
                                key={user.id}
                                className={styles.personAvatarWrapper}
                                style={{ zIndex: selectedUserDetails.length - index }}
                                data-tooltip={user.name}
                            >
                                <img
                                    src={user.avatar || default_avatar}
                                    alt={user.name}
                                    className={styles.personAvatarDisplay}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    isGuideMode ? (
                        <span className={styles.personPlaceholder}>
                            Select
                            <img src={chevron_down} alt="Chevron down" className={styles.personCellChevronDown} />
                        </span>
                    ) : null
                )}
            </div>

            {isOpen && createPortal(dropdownContent, document.body)}
        </div>
    );
}

/* File upload cell */
export const FileCell = ({ value, onChange, onContextMenu, isGuideMode }) => {
    const { showToast } = useToast();

    const fileInputRef = useRef(null);
    const files = Array.isArray(value) ? value : (value ? [value] : []);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {

            // Create FormData to send the file
            const formData = new FormData();
            formData.append('image', file);

            // Upload to S3
            const response = await api.upload('/images', formData);

            // Use the URL returned from S3
            const uploadedUrl = response.url;
            const newFile = {
                fileObj: file,
                name: file.name,
                type: file.type,
                preview: uploadedUrl, // Use the real S3 URL for preview
                url: uploadedUrl, // Store the real URL for persistence
                isImage: file.type.startsWith('image/')
            };
            onChange([...files, newFile]);

        } catch (error) {
            console.error("Failed to upload file:", error);
            showToast('Failed to upload file. Please try again.', 'error');
        }

        // Reset input so the same file can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemove = (indexToRemove) => {
        const newFiles = files.filter((_, index) => index !== indexToRemove);
        // If empty, pass null or empty array depending on preference. 
        // Keeping it as empty array for consistency with multi-file logic.
        onChange(newFiles.length > 0 ? newFiles : null);
    };

    return (
        <div
            className={`${styles.fileCellWrapper} ${isGuideMode ? styles.guideMode : ''}`}
            onContextMenu={onContextMenu}
            onClick={() => {
                if (files.length === 0 && fileInputRef.current) {
                    fileInputRef.current.click();
                }
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                className={styles.hiddenInput}
                onChange={handleFileChange}
                accept="image/png, image/jpeg, image/gif, image/webp, .pdf, .doc, .docx, .txt"
            />

            <div className={styles.fileList}>
                {files.map((file, index) => (
                    <div key={index} className={styles.filePreviewContainer}>
                        {file.isImage ? (
                            <div className={styles.fileImageContainer}>
                                <div className={styles.fileImageWrapper}>
                                    <img
                                        src={file.preview}
                                        alt={file.name}
                                        className={styles.filePreviewImage}
                                        title={file.name}
                                    />
                                </div>
                                <span className={styles.fileImageName}>{file.name}</span>
                            </div>
                        ) : (
                            <div className={styles.fileGenericWrapper} title={file.name}>
                                <span className={styles.fileGenericIcon}>📄</span>
                                <span className={styles.fileGenericName}>{file.name}</span>
                            </div>
                        )}

                        <button
                            className={styles.fileDeleteButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(index);
                            }}
                            title="Remove file"
                        >
                            <img src={delete_icon} alt="Delete file" className={styles.fileDeleteIcon} />
                        </button>
                    </div>
                ))}
            </div>

            {/* Upload File Trigger - Always rendered, visibility handled by CSS */}
            <div
                className={styles.fileUploadTrigger}
                onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current.click();
                }}
                title="Upload file"
            >
                + Upload file
            </div>
        </div>
    );
};